import {
  forwardRef,
  useRef,
  useCallback,
  useState,
  useImperativeHandle,
  ChangeEventHandler,
} from 'react';

import { Inline } from '../../Inline/Inline';

import CalendarPopover from '../CalendarPopover/CalendarPopover';
import { externalDateValidator, getFormattedDatePart } from '../helpers';
import FieldWithHint from './FieldWithHint/FieldWithHint';
import type { MultipleDatepickerProps } from '../types';

/**
 * @deprecated Используйте MultipleFieldDatepicker
 */
export const MultipleDatepicker = forwardRef(
  (
    {
      disabled,
      value,
      errors = [],
      minDate,
      maxDate,
      disabledDates,
      initialCalendarType,
      onChange,
      className,
      ...fieldProps
    }: MultipleDatepickerProps,
    ref
  ) => {
    const initialInnerValue = {
      day: '',
      month: '',
      year: '',
    };
    const [innerValue, setInnerValue] = useState(value);
    const dayRef = useRef<HTMLInputElement>(null);
    const monthRef = useRef<HTMLInputElement>(null);
    const yearRef = useRef<HTMLInputElement>(null);
    const onFieldChange = useCallback(
      (
        type: 'day' | 'month' | 'year'
      ): ChangeEventHandler<HTMLInputElement> => (event) => {
        const { value: userInputValue } = event.target;
        const newInnerValue = {
          ...innerValue,
          [type]: userInputValue,
        };
        setInnerValue(newInnerValue);

        const formattedDay = getFormattedDatePart(newInnerValue.day);
        const formattedMonth = getFormattedDatePart(newInnerValue.month);
        onChange({
          day: formattedDay,
          month: formattedMonth,
          year: newInnerValue.year,
        });
      },
      [innerValue, onChange]
    );

    useImperativeHandle(ref, () => ({
      focus() {
        dayRef.current?.focus();
      },
      get day() {
        return dayRef.current;
      },
      get month() {
        return monthRef.current;
      },
      get year() {
        return yearRef.current;
      },
    }));

    const formattedDate = `${value.year}-${value.month}-${value.day}`;
    const selectedCalendarDate = externalDateValidator(formattedDate)
      ? [formattedDate]
      : undefined;

    const handleCalendarChange = (date: string | undefined) => {
      if (date) {
        const [year, month, day] = date.split('-');
        const newDate = { day, month, year };
        setInnerValue(newDate);
        onChange(newDate);
      } else {
        setInnerValue(initialInnerValue);
        onChange(initialInnerValue);
      }
    };

    return (
      <Inline
        space="datepickerSpace"
        display="flex"
        alignItems="flex-end"
        className={className}
      >
        <Inline space="sp-8">
          <FieldWithHint
            {...fieldProps}
            ref={dayRef}
            hasError={errors.includes('day')}
            hint="День"
            visuallyHiddenHint="ДД"
            value={innerValue.day}
            disabled={disabled}
            onChange={onFieldChange('day')}
            styles={{ width: 'datepickerDayMonthFieldWidth' }}
          />
          <FieldWithHint
            {...fieldProps}
            ref={monthRef}
            hasError={errors.includes('month')}
            visuallyHiddenHint="ММ"
            hint="Месяц"
            value={innerValue.month}
            disabled={disabled}
            onChange={onFieldChange('month')}
            styles={{ width: 'datepickerDayMonthFieldWidth' }}
          />
          <FieldWithHint
            {...fieldProps}
            ref={yearRef}
            hasError={errors.includes('year')}
            visuallyHiddenHint="ГГГГ"
            hint="Год"
            value={innerValue.year}
            disabled={disabled}
            onChange={onFieldChange('year')}
            styles={{ width: 'datepickerYearFieldWidth' }}
          />
        </Inline>
        <CalendarPopover
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          disabledDates={disabledDates}
          initialCalendarType={initialCalendarType}
          selectedDate={selectedCalendarDate}
          onSelectedDateChange={handleCalendarChange}
        />
      </Inline>
    );
  }
);
